import React, { useState, useLayoutEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { USER_TYPES } from '@medflex/utils/constants';
import PhysicianTitleBar, {
  SupportChatToggle,
} from '@medflex/components/stories/PhysicianTitleBar';
import { trackEvent } from '@medflex/utils/Tracking';
import useIsExtraSmallScreen from '@medflex/hooks/useIsExtraSmallScreen';
import PropTypes from 'prop-types';
import useNavBarContext from '../hooks/useNavBarContext';
import useUserProfile from '../hooks/useUserProfile';
import useFlutterCallHandler from '../hooks/useFlutterCallHandler';
import useHubspotChat from '../hooks/useHubspotChat';

const PhysicianTitleBarModule = ({ className = '' }) => {
  const { toggle: toggleNavBar, isCollapsed, status } = useNavBarContext();
  const { userProfile } = useUserProfile();
  const shouldShowFullMenu = !useIsExtraSmallScreen();
  const supportChatToggleRef = useRef(null);
  const [chatTop, setChatTop] = useState(0);
  const history = useHistory();
  const { handleLogout: handleFlutterLogout } = useFlutterCallHandler();
  const {
    isReady: isHubspotApiReady,
    HubSpotConversations,
    unreadCount: supportChatUnreadCount,
  } = useHubspotChat();
  const [isHubspotChatOpen, setIsHubspotChatOpen] = useState(false);

  useLayoutEffect(() => {
    if (supportChatToggleRef.current) {
      const rect = supportChatToggleRef.current.getBoundingClientRect();
      setChatTop(rect.bottom);
    }
  }, [isHubspotApiReady, isHubspotChatOpen]);

  const handleConfigClicked = event => {
    trackEvent(event, { zone: 'PhysicianTitleBar' });
    history.push(`/settings/${userProfile.type === USER_TYPES.PHYSICIAN ? 'profile' : 'contact'}`);
  };

  const handleAppSettingsClicked = event => {
    trackEvent(event, { zone: 'PhysicianTitleBar' });
    history.push(`/app-settings/notifications`);
  };

  const handleLogout = event => {
    trackEvent(event, { zone: 'PhysicianTitleBar' });
    handleFlutterLogout();
    history.push('/abmelden');
  };

  const handleFaq = event => {
    trackEvent(event, { zone: 'PhysicianTitleBar' });
    window.open('https://medflex.de/faq-hilfe/');
  };

  const handleNewFeatures = event => {
    trackEvent(event, { zone: 'PhysicianTitleBar' });
    window.open('https://medflex.de/hilfe-category/behandler-neue-funktionen/');
  };

  const handleOpenCloseHubspotChat = () => {
    if (HubSpotConversations) {
      if (!isHubspotChatOpen) {
        setIsHubspotChatOpen(true);
        return;
      }

      if (isHubspotChatOpen) {
        setIsHubspotChatOpen(false);
      }
    }
  };

  return (
    status === 'loaded' && (
      <div className={className}>
        <PhysicianTitleBar
          avatar={userProfile.attributes.avatar}
          fullNameWithTitle={userProfile.attributes.fullNameWithTitle}
          initials={userProfile.attributes.initials}
          toggleMenu={toggleNavBar}
          isActive={!isCollapsed}
          closeIconShown={!isCollapsed && !shouldShowFullMenu}
          userType={userProfile.type}
          onClickConfig={handleConfigClicked}
          onClickAppSettings={handleAppSettingsClicked}
          onLogout={handleLogout}
          onClickFaq={handleFaq}
          onClickNewFeatures={handleNewFeatures}
        >
          <>
            {isHubspotApiReady && (
              <SupportChatToggle
                ref={supportChatToggleRef}
                onClick={handleOpenCloseHubspotChat}
                count={supportChatUnreadCount}
              />
            )}

            <div
              id="hubspot-chat"
              className={`${!isHubspotChatOpen ? 'invisible' : ''} fixed z-100 right-0 lg:p-2 lg:shadow-md`}
              style={{ top: `${chatTop}px` }}
            />
          </>
        </PhysicianTitleBar>
      </div>
    )
  );
};
PhysicianTitleBarModule.propTypes = {
  className: PropTypes.string,
};

export default PhysicianTitleBarModule;
